import { useMemo, useState } from 'react'
import { X, Loader2, Save, Ban, FileText, Lock, CheckCircle2 } from 'lucide-react'
import { postJson, patchJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { fullName } from '../../lib/employee.js'
import { canEdit, canVoid, fmtStamp, warningState } from '../../lib/warning.js'
import { bkkToday } from '../../lib/leaveRequest.js'
import SearchableSelect from '../SearchableSelect.jsx'
import AttachPicker from '../AttachPicker.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
// A locked field needs its OWN class string, never an override appended to FIELD:
// Tailwind resolves same-specificity conflicts by emission order and the palette
// colours land after the greys, so `${FIELD} bg-slate-100` renders white. Recorded
// in docs/CONVENTIONS.md after two fields in EmployeeEditor looked editable for months.
const FIELD_RO = 'w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-[13.5px] text-slate-500 outline-none cursor-not-allowed'

/**
 * Issue, edit or void a หนังสือเตือน.
 *
 * 🚨 There is NO DELETE, deliberately. A warning is the clinic's evidence in a
 * termination or a กรมแรงงาน case; withdrawing one is a VOID with a stated reason,
 * and the voided record stays visible and visibly voided. A register you can delete
 * rows from proves nothing.
 *
 * 🚨 EDITING STOPS AT รับทราบ. Once the employee has acknowledged, the text is
 * frozen — rewriting a document somebody has already signed off on is precisely
 * what destroys its value. Xano refuses it; this form stops offering it, and says
 * why rather than just greying out.
 *
 * The employee cannot be changed after creation: a warning re-pointed at a
 * different person would carry the first person's acknowledgement with it.
 */
export default function WarningEditor({ warning, employees = [], onClose, onSaved }) {
  const isNew = !warning
  const state = warning ? warningState(warning) : 'pending'
  const editable = isNew || canEdit(warning)

  const [employeeId, setEmployeeId] = useState(warning?.employee_id ? String(warning.employee_id) : '')
  const [subject, setSubject] = useState(warning?.subject || '')
  const [detail, setDetail] = useState(warning?.detail || '')
  const [incidentDate, setIncidentDate] = useState(warning?.incident_date?.slice(0, 10) || '')
  const [issueDate, setIssueDate] = useState(warning?.issue_date?.slice(0, 10) || bkkToday())
  const [files, setFiles] = useState(Array.isArray(warning?.file) ? warning.file : [])
  const [voidReason, setVoidReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [confirm, setConfirm] = useState(null)   // 'save' | 'void'

  // ACTIVE employees only — a warning is issued to someone who works here.
  // Two lines per row: code on top, full name beneath (SearchableSelect gives `hint`
  // its own muted line), matching the filter on the register. `search` carries both
  // halves plus the nickname, so typing either finds the person.
  const options = useMemo(
    () => employees
      .filter((e) => e.active !== false)
      .map((e) => ({
        value: String(e.id),
        label: e.code || `#${e.id}`,
        hint: fullName(e),
        search: `${e.code || ''} ${fullName(e)} ${e.nickname || ''} ${e.name || ''}`,
        selectedLabel: `${e.code || `#${e.id}`} — ${fullName(e)}`,
      })),
    [employees],
  )

  const target = employees.find((e) => String(e.id) === String(employeeId))

  async function save() {
    setConfirm(null)
    setBusy(true)
    setErr('')
    try {
      const payload = {
        subject: subject.trim(),
        detail: detail.trim(),
        incident_date: incidentDate || undefined,
        issue_date: issueDate || undefined,
        file: files,
      }
      if (isNew) await postJson('/api/hr/warning', { ...payload, employee_id: Number(employeeId) })
      else await patchJson(`/api/hr/warning/${warning.id}`, payload)
      onSaved?.()
    } catch (e) {
      setErr(apiErrorText(e, 'บันทึกไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  async function voidIt() {
    setConfirm(null)
    setBusy(true)
    setErr('')
    try {
      await patchJson(`/api/hr/warning/${warning.id}`, { void: true, void_reason: voidReason.trim() })
      onSaved?.()
    } catch (e) {
      setErr(apiErrorText(e, 'ยกเลิกไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  const canSave = !!employeeId && subject.trim().length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[520px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-[16px] font-semibold text-slate-900">
              {isNew ? 'ออกหนังสือเตือน' : 'หนังสือเตือน'}
            </h2>
            <p className="text-[12.5px] text-slate-500">
              {isNew ? 'บันทึกภายใน — ไม่ได้ออกเป็นเอกสารพิมพ์' : (target ? `${target.code} · ${fullName(target)}` : '')}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3.5">
          {!editable && (
            <div className="flex gap-2 rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5">
              <Lock size={14} className="text-slate-400 shrink-0 mt-0.5" />
              <p className="text-[12.5px] text-slate-600 leading-relaxed">
                {state === 'voided'
                  ? `ยกเลิกแล้วเมื่อ ${fmtStamp(warning.voided_at)}${warning.void_reason ? ` — ${warning.void_reason}` : ''}`
                  : 'พนักงานรับทราบหนังสือฉบับนี้แล้ว จึงแก้ไขข้อความไม่ได้ — หากต้องการถอน กรุณายกเลิกหนังสือพร้อมระบุเหตุผล'}
              </p>
            </div>
          )}

          <label className="block">
            <span className="block text-[12px] font-medium text-slate-500 mb-1">พนักงาน</span>
            {isNew ? (
              <SearchableSelect
                options={options}
                value={employeeId}
                onChange={setEmployeeId}
                placeholder="— เลือกพนักงาน —"
                searchPlaceholder="ค้นหาด้วยรหัสหรือชื่อ"
              />
            ) : (
              // Never re-pointable: the acknowledgement on this row belongs to one
              // person, and moving the row would carry it to somebody else.
              <input value={target ? `${target.code} — ${fullName(target)}` : `#${warning.employee_id}`} readOnly className={FIELD_RO} />
            )}
          </label>

          <label className="block">
            <span className="block text-[12px] font-medium text-slate-500 mb-1">เรื่อง</span>
            <input
              value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!editable}
              className={editable ? FIELD : FIELD_RO} placeholder="เช่น มาสายเกินกำหนดซ้ำหลายครั้ง"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[12px] font-medium text-slate-500 mb-1">วันที่เกิดเหตุ</span>
              <input type="date" value={incidentDate} onChange={(e) => setIncidentDate(e.target.value)} disabled={!editable} className={editable ? FIELD : FIELD_RO} />
            </label>
            <label className="block">
              <span className="block text-[12px] font-medium text-slate-500 mb-1">วันที่ออกหนังสือ</span>
              <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} disabled={!editable} className={editable ? FIELD : FIELD_RO} />
            </label>
          </div>

          <label className="block">
            <span className="block text-[12px] font-medium text-slate-500 mb-1">
              รายละเอียด <span className="font-normal text-slate-400">· พนักงานจะเห็นข้อความนี้</span>
            </span>
            <textarea
              rows={5} value={detail} onChange={(e) => setDetail(e.target.value)} disabled={!editable}
              className={`${editable ? FIELD : FIELD_RO} resize-y`}
              placeholder="อธิบายพฤติกรรม วันเวลา และสิ่งที่ต้องแก้ไข"
            />
          </label>

          {editable && employeeId ? (
            <AttachPicker
              files={files}
              onChange={setFiles}
              uploadUrl="/api/hr/warning-upload"
              extra={{ employee_id: employeeId }}
              hint="แนบสแกนหนังสือที่ลงนามแล้ว หรือหลักฐานประกอบ"
              disabled={busy}
            />
          ) : (
            Array.isArray(warning?.file) && warning.file.length > 0 && (
              <div>
                <div className="text-[11.5px] font-medium text-slate-400 mb-1.5">เอกสารแนบ</div>
                <div className="space-y-1">
                  {warning.file.map((f) => (
                    <a
                      key={f.uuid || f.location}
                      href={`/api/hr/file/${encodeURI(f.location || '')}`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 text-[13px] text-brand-600 hover:text-brand-700 hover:underline"
                    >
                      <FileText size={13} /> {f.name || 'เอกสาร'}
                    </a>
                  ))}
                </div>
              </div>
            )
          )}

          {warning?.acknowledged_at && (
            <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-[12.5px] text-emerald-800 font-medium">
                <CheckCircle2 size={13} /> พนักงานรับทราบเมื่อ {fmtStamp(warning.acknowledged_at)}
              </div>
              {warning.acknowledged_ip && (
                <div className="text-[11px] text-emerald-700/80 mt-0.5">จาก IP {warning.acknowledged_ip}</div>
              )}
              {warning.acknowledged_note && (
                <div className="mt-1.5 text-[12.5px] text-slate-700 whitespace-pre-wrap break-words">
                  <span className="text-slate-500">ความเห็นของพนักงาน: </span>{warning.acknowledged_note}
                </div>
              )}
            </div>
          )}

          {!isNew && canVoid(warning) && (
            <label className="block">
              <span className="block text-[12px] font-medium text-slate-500 mb-1">เหตุผลในการยกเลิกหนังสือ</span>
              <input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} className={FIELD} placeholder="เช่น ออกผิดคน / ตรวจสอบแล้วไม่เป็นความจริง" />
            </label>
          )}

          {err && <p className="text-[12.5px] text-rose-600">{err}</p>}
        </div>

        <div className="border-t border-slate-100 px-5 py-3.5 flex items-center justify-between gap-2">
          {!isNew && canVoid(warning) ? (
            <button
              onClick={() => setConfirm('void')} disabled={busy || !voidReason.trim()}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-medium text-rose-600 ring-1 ring-rose-200 hover:bg-rose-50 disabled:opacity-40"
            >
              <Ban size={14} /> ยกเลิกหนังสือ
            </button>
          ) : <span />}

          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ปิด</button>
            {editable && (
              <button
                onClick={() => setConfirm('save')} disabled={busy || !canSave}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {isNew ? 'ออกหนังสือเตือน' : 'บันทึก'}
              </button>
            )}
          </div>
        </div>
      </div>

      {confirm === 'save' && (
        <ConfirmDialog
          title={isNew ? 'ออกหนังสือเตือนฉบับนี้?' : 'บันทึกการแก้ไข?'}
          message={
            isNew
              ? `ถึง ${target ? `${target.code} — ${fullName(target)}` : ''}\nเรื่อง “${subject.trim()}”\n\n` +
                'พนักงานจะเห็นหนังสือฉบับนี้ในพื้นที่ของฉันทันที และต้องกดรับทราบ ' +
                'เมื่อรับทราบแล้วจะแก้ไขข้อความไม่ได้อีก'
              : 'พนักงานจะเห็นข้อความที่แก้ไขแล้ว'
          }
          confirmLabel={isNew ? 'ออกหนังสือ' : 'บันทึก'}
          onConfirm={save}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'void' && (
        <ConfirmDialog
          title="ยกเลิกหนังสือเตือนฉบับนี้?"
          message={`“${warning?.subject}”\n\nหนังสือจะยังคงอยู่ในระบบแต่แสดงว่ายกเลิกแล้ว พร้อมเหตุผลที่ระบุ — ไม่ได้ลบออกจากประวัติ`}
          confirmLabel="ยกเลิกหนังสือ"
          tone="danger"
          onConfirm={voidIt}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}
